import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth, apiClient } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
    DropdownMenuSeparator, DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { TransactionHistory } from '@/components/TransactionHistory';
import { MentionTextarea, RenderNoteBody } from '@/components/MentionTextarea';
import { ClickToCall, CallHistory } from '@/components/ClickToCall';
import { CallRecordings } from '@/components/CallRecordings';
import CallAnalyticsWidget from '@/components/CallAnalyticsWidget';
import ReminderModal from '@/components/ReminderModal';
import { LastActivityBadge } from '@/components/LastActivityBadge';
import { Pagination } from '@/components/Pagination';
import {
    DndContext, DragOverlay, closestCorners, KeyboardSensor, PointerSensor,
    useSensor, useSensors, useDroppable,
} from '@dnd-kit/core';
import {
    SortableContext, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    Search, Phone, PhoneCall, Mail, User, Briefcase, MessageSquare, TrendingUp,
    CheckCircle, DollarSign, MoreVertical, GripVertical, RefreshCw, Bell, Clock,
    Send, FileText, AlertTriangle, PhoneOff, Star, Shield, Loader2,
} from 'lucide-react';

const BD_STAGES = [
    { id: 'new_student', label: 'New Student', color: 'bg-sky-500', icon: User },
    { id: 'contacted', label: 'Contacted', color: 'bg-violet-500', icon: MessageSquare },
    { id: 'pitched', label: 'Pitched', color: 'bg-amber-500', icon: TrendingUp },
    { id: 'interested', label: 'Interested', color: 'bg-lime-500', icon: CheckCircle },
    { id: 'closed', label: 'Closed', color: 'bg-emerald-500', icon: DollarSign },
];

const PITCH_TYPE_STYLES = {
    redeposit: { label: 'Redeposit', border: 'border-l-4 border-l-emerald-500', badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
    upgrade: { label: 'Upgrade', border: 'border-l-4 border-l-violet-500', badge: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300' },
    mix: { label: 'Redeposit + Upgrade', border: 'border-l-4 border-l-amber-500', badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
};

const COLOR_TAG_STYLES = {
    handle_with_care: { label: 'Handle With Care', color: 'bg-amber-100 border-amber-400 text-amber-800', dot: 'bg-amber-500', icon: AlertTriangle },
    do_not_disturb: { label: 'Do Not Disturb', color: 'bg-red-100 border-red-400 text-red-800', dot: 'bg-red-500', icon: PhoneOff },
    vip: { label: 'VIP Client', color: 'bg-yellow-100 border-yellow-500 text-yellow-800', dot: 'bg-yellow-500', icon: Star },
    priority: { label: 'Priority', color: 'bg-purple-100 border-purple-400 text-purple-800', dot: 'bg-purple-500', icon: Shield },
    follow_up: { label: 'Follow Up', color: 'bg-blue-100 border-blue-400 text-blue-800', dot: 'bg-blue-500', icon: Bell },
};

const fmtAED = (v) => new Intl.NumberFormat('en-AE', { style: 'currency', currency: 'AED', minimumFractionDigits: 0 }).format(v || 0);

const BD_ROLES_SET = new Set(['business_development', 'business_development_manager_']);

const BDStudentCard = ({ student, onView, isDragging, isSuperAdmin, bdAgents, onReassign }) => {
    const colorTag = student.color_tag ? COLOR_TAG_STYLES[student.color_tag] : null;
    const isInClosed = student.bd_stage === 'closed';
    const hasRedeposited = (student.redeposit_count || 0) > 0;
    const isShadow = student.is_shadow;
    const pitchStyle = student.bd_pitch_type ? PITCH_TYPE_STYLES[student.bd_pitch_type] : null;
    const isPitchedOrBeyond = ['pitched', 'interested', 'closed'].includes(student.bd_stage);
    return (
        <div
            className={`kanban-card stage-${student.bd_stage} animate-fade-in cursor-pointer ${isDragging ? 'opacity-50 shadow-lg ring-2 ring-primary' : ''} ${isShadow ? 'bg-amber-50 dark:bg-amber-950/30 border-2 border-amber-400 dark:border-amber-600 ring-1 ring-amber-300/30 opacity-80' : isInClosed ? 'bg-emerald-50 dark:bg-emerald-950/30 border-2 border-emerald-400 dark:border-emerald-600 ring-1 ring-emerald-300/30' : colorTag ? `${colorTag.color.split(' ')[0]} border-2 ${colorTag.color.split(' ')[1]}` : ''} ${isPitchedOrBeyond && pitchStyle ? pitchStyle.border : ''}`}
            onClick={() => !isDragging && !isShadow && onView(student)}
            data-testid={`bd-card-${student.id}`}
        >
            {isShadow && (
                <div className="-mx-3 -mt-3 mb-2 px-3 py-1 flex items-center gap-1.5 text-[10px] font-semibold rounded-t-md bg-amber-500 text-white">
                    <DollarSign className="h-3 w-3" /> Redeposit — {student.date ? new Date(student.date).toLocaleDateString() : ''} — {fmtAED(student.amount)}
                </div>
            )}
            {!isShadow && isInClosed && (
                <div className="-mx-3 -mt-3 mb-2 px-3 py-1 flex items-center gap-1.5 text-[10px] font-semibold rounded-t-md bg-emerald-500 text-white">
                    <DollarSign className="h-3 w-3" /> Awaiting Redeposit Recording
                </div>
            )}
            {!isInClosed && colorTag && (
                <div className={`-mx-3 -mt-3 mb-2 px-3 py-1 flex items-center gap-1.5 text-[10px] font-semibold rounded-t-md ${colorTag.color}`}>
                    <colorTag.icon className="h-3 w-3" />
                    {colorTag.label}
                </div>
            )}
            <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                    <div className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground">
                        <GripVertical className="h-4 w-4" />
                    </div>
                    <div className="w-8 h-8 rounded-full bg-sky-600 text-white flex items-center justify-center text-sm font-medium">
                        {student.full_name?.charAt(0) || '?'}
                    </div>
                    <div>
                        <div className="flex items-center gap-1">
                            <p className="font-medium text-sm">{student.full_name}</p>
                            {hasRedeposited && (
                                <Badge className="bg-emerald-500 text-white text-[10px] px-1 py-0">
                                    <DollarSign className="h-2 w-2 mr-0.5" />x{student.redeposit_count}
                                </Badge>
                            )}
                            {isPitchedOrBeyond && pitchStyle && (
                                <Badge className={`text-[10px] px-1.5 py-0 ${pitchStyle.badge}`}>
                                    {pitchStyle.label}
                                </Badge>
                            )}
                        </div>
                        <p className="text-xs text-muted-foreground">{student.package_bought || student.current_course_name || 'N/A'}</p>
                    </div>
                </div>
                {/* 3-dot actions menu — for shadow redeposit cards we still
                    show it, but "View Details" opens the underlying real
                    students row (via student.student_id) so updates route
                    to a valid id and pitch-type changes succeed. */}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-6 w-6">
                            <MoreVertical className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onView(student); }}>
                            View Details
                        </DropdownMenuItem>
                        {isSuperAdmin && bdAgents && bdAgents.length > 0 && (
                            <>
                                <DropdownMenuSeparator />
                                <DropdownMenuLabel className="text-xs text-muted-foreground">Reassign to:</DropdownMenuLabel>
                                {bdAgents.filter(a => a.id !== student.bd_agent_id).map(a => (
                                    <DropdownMenuItem
                                        key={a.id}
                                        onClick={(e) => { e.stopPropagation(); onReassign(student.id, a.id); }}
                                        data-testid={`bd-reassign-${student.id}-to-${a.id}`}
                                    >
                                        <RefreshCw className="h-3 w-3 mr-2" />
                                        {a.full_name}
                                    </DropdownMenuItem>
                                ))}
                            </>
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            <div className="space-y-1.5 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                    <Phone className="h-3 w-3" />
                    <span className="font-mono text-xs">{student.phone}</span>
                </div>
                {student.cs_agent_name && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                        <User className="h-3 w-3 text-emerald-500" />
                        <span className="text-xs">CS: {student.cs_agent_name}</span>
                    </div>
                )}
                {student.mentor_name && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                        <Briefcase className="h-3 w-3" />
                        <span className="text-xs">Mentor: {student.mentor_name}</span>
                    </div>
                )}
            </div>

            <div className="mt-2" data-testid={`bd-activity-${student.id}`}>
                <LastActivityBadge at={student.last_activity} by={student.last_activity_by} />
            </div>

            <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                <div className="flex items-center gap-1.5 flex-wrap">
                    {student.last_redeposit_amount > 0 ? (
                        <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30 text-xs font-mono" data-testid={`bd-last-redep-${student.id}`}>
                            <DollarSign className="h-3 w-3 mr-0.5" />
                            AED {student.last_redeposit_amount?.toLocaleString()}
                        </Badge>
                    ) : student.ltv_total > 0 ? (
                        <Badge variant="outline" className="text-xs font-mono border-sky-300 text-sky-700 dark:text-sky-300" data-testid={`bd-ltv-${student.id}`}>
                            <DollarSign className="h-3 w-3 mr-0.5" />
                            AED {student.ltv_total?.toLocaleString()}
                        </Badge>
                    ) : (
                        <span className="text-xs text-muted-foreground">
                            {student.enrollment_amount ? fmtAED(student.enrollment_amount) : 'No deposit'}
                        </span>
                    )}
                    {student.txn_count > 0 && (
                        <Badge variant="outline" className="text-[10px] border-slate-300 text-muted-foreground">
                            {student.txn_count} txn{student.txn_count !== 1 ? 's' : ''}
                        </Badge>
                    )}
                    {student.reminder_date && !student.reminder_completed && (
                        <Badge className="bg-amber-500 text-white text-[10px] px-1.5 py-0">
                            <Bell className="h-2.5 w-2.5 mr-0.5" />
                            {student.reminder_date}
                        </Badge>
                    )}
                </div>
                {student.bd_agent_name && (
                    <Badge variant="outline" className="text-xs">{student.bd_agent_name}</Badge>
                )}
            </div>
        </div>
    );
};

const SortableBDCard = ({ student, onView, isSuperAdmin, bdAgents, onReassign }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: student.id });
    const style = { transform: CSS.Transform.toString(transform), transition };
    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            <BDStudentCard student={student} onView={onView} isDragging={isDragging}
                isSuperAdmin={isSuperAdmin} bdAgents={bdAgents} onReassign={onReassign} />
        </div>
    );
};

const BD_COLUMN_PAGE_SIZE = 50;

const BDKanbanColumn = ({ stage, baseParams, shadowCards, onView, isSuperAdmin, bdAgents, onReassign }) => {
    const [colPage, setColPage] = React.useState(1);
    const [colStudents, setColStudents] = React.useState([]);
    const [colTotal, setColTotal] = React.useState(0);
    const [colLoading, setColLoading] = React.useState(true);
    const StageIcon = stage.icon;

    React.useEffect(() => {
        let cancelled = false;
        const fetchColumnData = async () => {
            setColLoading(true);
            try {
                const res = await apiClient.get('/bd/students', {
                    params: { ...baseParams, bd_stage: stage.id, page: colPage, page_size: BD_COLUMN_PAGE_SIZE }
                });
                if (cancelled) return;
                const data = res.data;
                const items = data?.items || (Array.isArray(data) ? data : []);
                setColStudents(items);
                setColTotal(data?.total || items.length);
            } catch {
                if (!cancelled) setColStudents([]);
            }
            if (!cancelled) setColLoading(false);
        };
        fetchColumnData();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [colPage, baseParams?.bd_agent_id, baseParams?.date_from, baseParams?.date_to, baseParams?.date_field, baseParams?.search, stage.id]);

    // For Closed column, merge in shadow cards on page 1
    const displayStudents = stage.id === 'closed' && shadowCards?.length > 0 && colPage === 1
        ? [...shadowCards, ...colStudents.filter(s => !s.is_shadow)]
        : colStudents;

    const totalForDisplay = stage.id === 'closed' && shadowCards?.length > 0
        ? colTotal + shadowCards.length
        : colTotal;
    const totalColPages = Math.ceil(colTotal / BD_COLUMN_PAGE_SIZE);
    const studentIds = displayStudents.map(s => s.id);
    const { setNodeRef, isOver } = useDroppable({ id: stage.id });

    return (
        <div ref={setNodeRef}
            className={`flex flex-col min-w-0 flex-1 bg-muted/50 rounded-xl p-3 ${isOver ? 'ring-2 ring-primary ring-offset-2' : ''}`}
            data-testid={`bd-column-${stage.id}`}
            style={{ minHeight: '400px', maxHeight: 'calc(100vh - 280px)' }}
        >
            <div className="flex items-center justify-between mb-3 pb-2 border-b border-border">
                <div className="flex items-center gap-2">
                    <StageIcon className={`h-4 w-4 ${stage.color.replace('bg-', 'text-')}`} />
                    <h3 className="font-semibold text-sm">{stage.label}</h3>
                </div>
                <Badge variant="secondary" className="text-xs">{totalForDisplay}</Badge>
            </div>
            {colLoading ? (
                <div className="flex-1 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary" />
                </div>
            ) : (
                <ScrollArea className="flex-1">
                    <SortableContext items={studentIds} strategy={verticalListSortingStrategy}>
                        <div className="space-y-2 min-h-[80px] p-0.5">
                            {displayStudents.map(s => (
                                <SortableBDCard key={s.id} student={s} onView={onView}
                                    isSuperAdmin={isSuperAdmin} bdAgents={bdAgents} onReassign={onReassign} />
                            ))}
                            {displayStudents.length === 0 && (
                                <div className={`text-center text-muted-foreground py-6 text-xs border-2 border-dashed rounded-lg transition-colors ${isOver ? 'border-primary bg-primary/5' : 'border-muted'}`}>
                                    Drop students here
                                </div>
                            )}
                        </div>
                    </SortableContext>
                </ScrollArea>
            )}
            {totalColPages > 1 && (
                <div className="flex items-center justify-between pt-2 mt-2 border-t border-border">
                    <button onClick={() => setColPage(p => Math.max(1, p - 1))} disabled={colPage <= 1}
                        className="text-xs px-2 py-1 rounded bg-background border hover:bg-accent disabled:opacity-40"
                        data-testid={`bd-col-prev-${stage.id}`}>Prev</button>
                    <span className="text-[10px] text-muted-foreground">
                        {(colPage - 1) * BD_COLUMN_PAGE_SIZE + 1}-{Math.min(colPage * BD_COLUMN_PAGE_SIZE, colTotal)} of {colTotal}
                    </span>
                    <button onClick={() => setColPage(p => Math.min(totalColPages, p + 1))} disabled={colPage >= totalColPages}
                        className="text-xs px-2 py-1 rounded bg-background border hover:bg-accent disabled:opacity-40"
                        data-testid={`bd-col-next-${stage.id}`}>Next</button>
                </div>
            )}
        </div>
    );
};

export default function BDCRMPage() {
    const { user } = useAuth();
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [bdAgents, setBdAgents] = useState([]);
    const [filterAgent, setFilterAgent] = useState('all');
    // Unified student-type filter: 'all' | 'new' | 'redeposit'
    const [studentTypeFilter, setStudentTypeFilter] = useState('all');
    // Unified time filter: { kind: 'all'|'today'|'this_week'|'this_month'|'month', value?: 'YYYY-MM' }
    const [timeFilter, setTimeFilter] = useState({ kind: 'all' });
    const [activeId, setActiveId] = useState(null);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showRedepositModal, setShowRedepositModal] = useState(false);
    const [recordingRedeposit, setRecordingRedeposit] = useState(false);
    const [redepositData, setRedepositData] = useState({ amount_aed: '', date: new Date().toISOString().slice(0, 10), deposit_type: 'redeposit' });
    // Resolve unified time filter into { date_from, date_to } or null.
    // Always uses enrolled_at semantics ("when did this student enter the
    // pipeline"). The Closed column overrides date_field to last_redeposit_date
    // via closedBaseParams below.
    const resolvedTimeRange = useMemo(() => {
        const today = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        switch (timeFilter.kind) {
            case 'today': {
                const t = ymd(today);
                return { date_from: t, date_to: t };
            }
            case 'this_week': {
                const dow = today.getDay() || 7;
                const start = new Date(today); start.setDate(today.getDate() - (dow - 1));
                const end = new Date(start); end.setDate(start.getDate() + 6);
                return { date_from: ymd(start), date_to: ymd(end) };
            }
            case 'this_month': {
                const start = new Date(today.getFullYear(), today.getMonth(), 1);
                const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                return { date_from: ymd(start), date_to: ymd(end) };
            }
            case 'month': {
                if (!timeFilter.value) return null;
                const [y, m] = timeFilter.value.split('-').map(Number);
                const start = new Date(y, m - 1, 1);
                const end = new Date(y, m, 0);
                return { date_from: ymd(start), date_to: ymd(end) };
            }
            default:
                return null;
        }
    }, [timeFilter]);
    const [studentNotes, setStudentNotes] = useState([]);
    const [newNote, setNewNote] = useState('');
    const [savingNote, setSavingNote] = useState(false);
    const [highlightNoteId, setHighlightNoteId] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const [showReminderModal, setShowReminderModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [allTimeTotals, setAllTimeTotals] = useState({ total: 0, stages: {}, revenue: 0 });
    const [shadowCards, setShadowCards] = useState([]);

    const isSuperAdmin = ['super_admin', 'admin'].includes(user?.role);
    const isBD = BD_ROLES_SET.has(user?.role);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor)
    );

    useEffect(() => {
        fetchStudents();
        fetchShadowCards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterAgent, resolvedTimeRange, studentTypeFilter, currentPage, pageSize]);

    useEffect(() => {
        if (isSuperAdmin || isBD) {
            apiClient.get('/bd/agents').then(r => setBdAgents(r.data || [])).catch(() => {});
        }
        // Fetch all-time stage totals (unfiltered)
        const agentParam = (isBD && !isSuperAdmin) ? '' : (filterAgent !== 'all' ? `&bd_agent_id=${filterAgent}` : '');
        apiClient.get(`/bd/dashboard?period=all_time${agentParam}`).then(r => {
            const d = r.data || {};
            setAllTimeTotals({
                total: d.total_students || 0,
                stages: d.stage_counts || {},
                revenue: d.all_time_revenue || 0,
                totalRedeposits: d.total_redeposit_count || 0,
            });
        }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterAgent]);

    const fetchStudents = async () => {
        try {
            setLoading(true);
            const params = { page: currentPage, page_size: pageSize };
            if (filterAgent !== 'all') params.bd_agent_id = filterAgent;
            if (searchTerm) params.search = searchTerm;
            if (studentTypeFilter !== 'all') params.student_type = studentTypeFilter;
            if (resolvedTimeRange) {
                params.date_from = resolvedTimeRange.date_from;
                params.date_to = resolvedTimeRange.date_to;
                params.date_field = 'enrolled_at';
            }
            const res = await apiClient.get('/bd/students', { params });
            const data = res.data;
            const items = data?.items || (Array.isArray(data) ? data : []);
            setStudents(items);
            setTotalRecords(data?.total || items.length);
            setTotalPages(data?.total_pages || 0);
        } catch (err) {
            toast.error('Failed to load BD students');
        } finally {
            setLoading(false);
        }
    };

    const handleDragStart = (e) => setActiveId(e.active.id);
    const handleDragEnd = async (e) => {
        setActiveId(null);
        const { active, over } = e;
        if (!over || !active) return;
        const studentId = active.id;
        let newStage = over.id;
        const student = students.find(s => s.id === studentId);
        if (!student) return;

        // If dropped on a student card, find which stage that student belongs to
        if (!BD_STAGES.find(s => s.id === newStage)) {
            const targetStudent = students.find(s => s.id === newStage);
            if (targetStudent) {
                newStage = targetStudent.bd_stage;
            } else {
                return;
            }
        }

        if (student.bd_stage === newStage) return;

        // Optimistic update
        setStudents(prev => prev.map(s => s.id === studentId ? { ...s, bd_stage: newStage } : s));
        try {
            await apiClient.put(`/bd/students/${studentId}/stage`, { bd_stage: newStage });
            toast.success(`Moved to ${BD_STAGES.find(s => s.id === newStage)?.label}`);
        } catch (err) {
            toast.error('Failed to update stage');
            fetchStudents();
        }
    };

    const handleReassign = async (studentId, newAgentId) => {
        try {
            await apiClient.post(`/bd/students/${studentId}/reassign?new_bd_agent_id=${newAgentId}`);
            toast.success('Student reassigned');
            fetchStudents();
        } catch (err) {
            toast.error('Failed to reassign');
        }
    };

    const handleRecordRedeposit = async () => {
        if (!selectedStudent || !redepositData.amount_aed) return;
        if (recordingRedeposit) return;  // double-click guard
        setRecordingRedeposit(true);
        try {
            await apiClient.post('/bd/record-redeposit', {
                student_id: selectedStudent.id,
                amount_aed: parseFloat(redepositData.amount_aed),
                amount: parseFloat(redepositData.amount_aed),
                date: redepositData.date,
                deposit_type: redepositData.deposit_type || 'redeposit',
            });
            const typeLabel = redepositData.deposit_type === 'upgrade' ? 'Upgrade' : redepositData.deposit_type === 'mix' ? 'Mix' : 'Redeposit';
            toast.success(`${typeLabel} recorded — student moved back to New Students`);
            setShowRedepositModal(false);
            setRedepositData({ amount_aed: '', date: new Date().toISOString().slice(0, 10), deposit_type: 'redeposit' });
            fetchStudents();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to record redeposit');
        } finally {
            setRecordingRedeposit(false);
        }
    };

    const handleViewStudent = async (student, opts = {}) => {
        // Shadow redeposit cards carry the real students.id in `student_id`.
        // Open the underlying real row so updates (stage / pitch_type) hit
        // a valid id and don't 404 with `shadow_bd_*`.
        if (student?.is_shadow && student.student_id) {
            try {
                const res = await apiClient.get(`/students/${student.student_id}`);
                const real = res?.data;
                if (real) {
                    setSelectedStudent(real);
                    setShowDetailModal(true);
                    setNewNote('');
                    setHighlightNoteId(opts.highlightNoteId || null);
                    fetchStudentNotes(real.id);
                    return;
                }
            } catch {
                toast.error('Could not load the underlying student record.');
                return;
            }
        }
        setSelectedStudent(student);
        setShowDetailModal(true);
        setNewNote('');
        setHighlightNoteId(opts.highlightNoteId || null);
        fetchStudentNotes(student.id);
    };

    // Deep-link: open student + highlight note when arriving from a mention notification
    useEffect(() => {
        const studentId = searchParams.get('student_id');
        const noteId = searchParams.get('note_id');
        if (!studentId) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await apiClient.get(`/students/${studentId}`);
                if (!cancelled && res?.data) {
                    handleViewStudent(res.data, { highlightNoteId: noteId });
                    const clean = new URLSearchParams(searchParams);
                    clean.delete('student_id');
                    clean.delete('note_id');
                    setSearchParams(clean, { replace: true });
                }
            } catch {
                toast.error('Could not open referenced student');
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!highlightNoteId || !studentNotes.length) return;
        const el = document.querySelector(`[data-note-id="${highlightNoteId}"]`);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.classList.add('note-pulse-highlight');
            const t = setTimeout(() => {
                el.classList.remove('note-pulse-highlight');
                setHighlightNoteId(null);
            }, 3200);
            return () => clearTimeout(t);
        }
    }, [highlightNoteId, studentNotes]);

    const fetchStudentNotes = async (studentId) => {
        try {
            const res = await apiClient.get(`/students/${studentId}/notes`);
            setStudentNotes(Array.isArray(res.data) ? res.data : []);
        } catch { setStudentNotes([]); }
    };

    const handleAddNote = async () => {
        if (!newNote.trim() || !selectedStudent) return;
        setSavingNote(true);
        try {
            await apiClient.post(`/students/${selectedStudent.id}/notes`, { text: newNote, type: 'call_note' });
            toast.success('Note saved');
            setNewNote('');
            fetchStudentNotes(selectedStudent.id);
        } catch { toast.error('Failed to save note'); }
        setSavingNote(false);
    };

    const handleReminderSuccess = () => {
        fetchStudents();
        setShowReminderModal(false);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchStudents();
        fetchShadowCards();
    };

    const fetchShadowCards = async () => {
        try {
            const params = {};
            if (!isSuperAdmin && isBD) params.bd_agent_id = user.id;
            if (filterAgent && filterAgent !== 'all') params.bd_agent_id = filterAgent;
            if (resolvedTimeRange) {
                params.date_from = resolvedTimeRange.date_from;
                params.date_to = resolvedTimeRange.date_to;
            }
            const res = await apiClient.get('/bd/redeposit-shadows', { params });
            setShadowCards(res.data?.items || []);
        } catch {
            setShadowCards([]);
        }
    };

    const baseParams = useMemo(() => {
        const p = {};
        if (!isSuperAdmin && isBD) p.bd_agent_id = user?.id;
        if (filterAgent && filterAgent !== 'all') p.bd_agent_id = filterAgent;
        if (searchTerm) p.search = searchTerm;
        if (studentTypeFilter !== 'all') p.student_type = studentTypeFilter;
        // When user explicitly picks a time filter (anything other than All
        // Time), apply it to every Kanban column too. We use enrolled_at as
        // the common field — that's when a student entered the BD pipeline.
        if (resolvedTimeRange && timeFilter.kind !== 'all') {
            p.date_from = resolvedTimeRange.date_from;
            p.date_to = resolvedTimeRange.date_to;
            p.date_field = 'enrolled_at';
        }
        return p;
    }, [isSuperAdmin, isBD, user?.id, filterAgent, searchTerm, studentTypeFilter, resolvedTimeRange, timeFilter.kind]);

    // Params for the CLOSED column — uses unified time filter, defaults to current month
    const closedBaseParams = useMemo(() => {
        const p = { ...baseParams };
        if (resolvedTimeRange) {
            p.date_from = resolvedTimeRange.date_from;
            p.date_to = resolvedTimeRange.date_to;
        } else {
            const now = new Date();
            p.date_from = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
            const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
            p.date_to = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
        }
        p.date_field = 'last_redeposit_date';
        return p;
    }, [baseParams, resolvedTimeRange]);

    // Backend already applies student_type filter; this is a no-op aliasing
    // kept so existing JSX that references `filteredStudents` keeps working.
    const filteredStudents = students;
    const draggedStudent = students.find(s => s.id === activeId);

    return (
        <div className="space-y-6" data-testid="bd-crm-page">
            {/* Header */}
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                    <h1 className="text-2xl font-bold">Business Development CRM</h1>
                    <p className="text-muted-foreground text-sm">
                        {isBD ? `My Students (${students.length})` : `All BD Students (${students.length})`}
                    </p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    {/* Unified Time Filter */}
                    <Select
                        value={timeFilter.kind === 'month' ? `month:${timeFilter.value}` : timeFilter.kind}
                        onValueChange={(v) => {
                            setCurrentPage(1);
                            if (v.startsWith('month:')) {
                                setTimeFilter({ kind: 'month', value: v.split(':')[1] });
                            } else {
                                setTimeFilter({ kind: v });
                            }
                        }}
                    >
                        <SelectTrigger className="w-[170px] h-8 text-xs" data-testid="bd-time-filter">
                            <Clock className="h-3.5 w-3.5 mr-1.5" />
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Time</SelectItem>
                            <SelectItem value="today">Today</SelectItem>
                            <SelectItem value="this_week">This Week</SelectItem>
                            <SelectItem value="this_month">This Month</SelectItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuLabel className="text-[10px] uppercase text-muted-foreground px-2 py-1">Specific Month</DropdownMenuLabel>
                            {(() => {
                                const opts = [];
                                const now = new Date();
                                let y = 2026, m = 0;
                                while (y < now.getFullYear() || (y === now.getFullYear() && m <= now.getMonth())) {
                                    const ym = `${y}-${String(m + 1).padStart(2, '0')}`;
                                    const lbl = new Date(y, m, 1).toLocaleString('en', { month: 'short', year: 'numeric' });
                                    opts.push(<SelectItem key={ym} value={`month:${ym}`}>{lbl}</SelectItem>);
                                    m++;
                                    if (m > 11) { m = 0; y++; }
                                }
                                return opts.reverse();
                            })()}
                        </SelectContent>
                    </Select>

                    {/* Unified Student-Type Filter */}
                    <Select
                        value={studentTypeFilter}
                        onValueChange={(v) => { setStudentTypeFilter(v); setCurrentPage(1); }}
                    >
                        <SelectTrigger className="w-[170px] h-8 text-xs" data-testid="bd-student-type-filter">
                            <DollarSign className="h-3.5 w-3.5 mr-1.5" />
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Students</SelectItem>
                            <SelectItem value="new">New Students</SelectItem>
                            <SelectItem value="redeposit">Redeposit Students</SelectItem>
                        </SelectContent>
                    </Select>

                    {isSuperAdmin && bdAgents.length > 0 && (
                        <Select value={filterAgent} onValueChange={setFilterAgent}>
                            <SelectTrigger className="w-[180px] h-8 text-xs" data-testid="bd-agent-filter">
                                <SelectValue placeholder="All BD Agents" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All BD Agents</SelectItem>
                                {bdAgents.map(a => (
                                    <SelectItem key={a.id} value={a.id}>{a.full_name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                </div>
            </div>

            {/* Call Stats */}
            <CallAnalyticsWidget compact />

            {/* All-Time Stats */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                <Card className="relative overflow-hidden" data-testid="bd-stat-total">
                    <CardContent className="pt-4 pb-3 px-4">
                        <div className="absolute top-0 left-0 w-1 h-full bg-primary" />
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Students</p>
                        <p className="text-2xl font-bold mt-1">{allTimeTotals.total}</p>
                        <p className="text-xs text-muted-foreground">{fmtAED(allTimeTotals.revenue)} revenue</p>
                    </CardContent>
                </Card>
                {BD_STAGES.map(st => (
                    <Card key={st.id} className="relative overflow-hidden" data-testid={`bd-stat-${st.id}`}>
                        <CardContent className="pt-4 pb-3 px-4">
                            <div className={`absolute top-0 left-0 w-1 h-full ${st.color}`} />
                            <p className="text-xs text-muted-foreground uppercase tracking-wider">{st.label}</p>
                            <p className="text-2xl font-bold mt-1">
                                {st.id === 'closed' ? (allTimeTotals.totalRedeposits || allTimeTotals.stages[st.id] || 0) : (allTimeTotals.stages[st.id] || 0)}
                            </p>
                            {st.id === 'closed' && allTimeTotals.stages[st.id] > 0 && (
                                <p className="text-xs text-muted-foreground">{allTimeTotals.stages[st.id]} awaiting</p>
                            )}
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Search */}
            <form onSubmit={handleSearch} className="flex gap-2 max-w-md">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="Search students..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                        className="pl-10" data-testid="bd-search-input" />
                </div>
                <Button type="submit" variant="secondary" data-testid="bd-search-btn">Search</Button>
            </form>

            {/* Kanban Board */}
            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary" />
                </div>
            ) : (
                <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                    <div className="flex gap-4 overflow-x-auto pb-4" data-testid="bd-kanban-board">
                        {BD_STAGES.map(stage => (
                            <BDKanbanColumn key={stage.id} stage={stage} baseParams={stage.id === 'closed' ? closedBaseParams : baseParams}
                                shadowCards={stage.id === 'closed' ? shadowCards : []}
                                onView={handleViewStudent} isSuperAdmin={isSuperAdmin} bdAgents={bdAgents}
                                onReassign={handleReassign} />
                        ))}
                    </div>
                    <DragOverlay>
                        {draggedStudent ? (
                            <BDStudentCard student={draggedStudent} onView={() => {}} isDragging
                                isSuperAdmin={false} bdAgents={[]} onReassign={() => {}} />
                        ) : null}
                    </DragOverlay>
                </DndContext>
            )}

            {/* Pagination */}
            {!loading && students.length > 0 && (
                <Pagination
                    page={currentPage}
                    totalPages={totalPages}
                    total={totalRecords}
                    pageSize={pageSize}
                    onPageChange={(p) => setCurrentPage(p)}
                    onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }}
                />
            )}

            {/* Student Detail Modal */}
            <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
                <DialogContent className="max-w-2xl max-h-[90vh]" data-testid="bd-student-detail-modal">
                    <DialogHeader className="sr-only"><DialogTitle>{selectedStudent?.full_name || 'Student Details'}</DialogTitle></DialogHeader>
                    {selectedStudent && (
                        <>
                            {/* Compact Header - Always Visible */}
                            <div className="flex items-center gap-3 pb-3 border-b">
                                <div className="w-10 h-10 rounded-full bg-sky-600 flex items-center justify-center text-white font-bold flex-shrink-0">
                                    {selectedStudent.full_name?.charAt(0) || '?'}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <h3 className="text-base font-semibold truncate">{selectedStudent.full_name}</h3>
                                        <Badge className={`text-[10px] ${BD_STAGES.find(s => s.id === selectedStudent.bd_stage)?.color}`}>
                                            {BD_STAGES.find(s => s.id === selectedStudent.bd_stage)?.label}
                                        </Badge>
                                    </div>
                                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                                        <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{selectedStudent.phone}</span>
                                        {selectedStudent.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{selectedStudent.email}</span>}
                                        <span>{selectedStudent.package_bought || selectedStudent.current_course_name || 'N/A'}</span>
                                    </div>
                                </div>
                                <ClickToCall
                                    phoneNumber={selectedStudent.phone}
                                    contactId={selectedStudent.id}
                                    contactName={selectedStudent.full_name}
                                    variant="outline"
                                    size="sm"
                                    showLabel={false}
                                />
                                <Button variant="outline" size="sm" className="text-xs gap-1" onClick={() => setShowReminderModal(true)} data-testid="bd-set-reminder-btn">
                                    <Bell className="h-3 w-3" /> Reminder
                                </Button>
                            </div>

                            {/* Tabbed Content */}
                            <Tabs defaultValue="notes" className="mt-2">
                                <TabsList className="grid w-full grid-cols-5 h-8">
                                    <TabsTrigger value="info" className="text-xs" data-testid="bd-tab-info">Info</TabsTrigger>
                                    <TabsTrigger value="notes" className="text-xs" data-testid="bd-tab-notes">
                                        <FileText className="h-3 w-3 mr-1" />Notes
                                    </TabsTrigger>
                                    <TabsTrigger value="transactions" className="text-xs" data-testid="bd-tab-transactions">
                                        <DollarSign className="h-3 w-3 mr-1" />Transactions
                                    </TabsTrigger>
                                    <TabsTrigger value="calls" className="text-xs" data-testid="bd-tab-calls">
                                        <PhoneCall className="h-3 w-3 mr-1" />Calls
                                    </TabsTrigger>
                                    <TabsTrigger value="update" className="text-xs" data-testid="bd-tab-update">Update</TabsTrigger>
                                </TabsList>

                                {/* INFO TAB */}
                                <TabsContent value="info" className="mt-3 space-y-4" data-testid="bd-tab-content-info">
                                    <div className="grid grid-cols-2 gap-3 text-sm">
                                        <div><span className="text-muted-foreground">Course:</span><span className="ml-2">{selectedStudent.package_bought || selectedStudent.current_course_name || 'N/A'}</span></div>
                                        <div><span className="text-muted-foreground">BD Agent:</span><span className="ml-2">{selectedStudent.bd_agent_name || 'Unassigned'}</span></div>
                                        <div><span className="text-muted-foreground">CS Agent:</span><span className="ml-2">{selectedStudent.cs_agent_name || 'N/A'}</span></div>
                                        <div><span className="text-muted-foreground">Mentor:</span><span className="ml-2">{selectedStudent.mentor_name || 'N/A'}</span></div>
                                        <div><span className="text-muted-foreground">Enrollment:</span><span className="ml-2 font-mono">{fmtAED(selectedStudent.enrollment_amount)}</span></div>
                                        <div><span className="text-muted-foreground">Language:</span><span className="ml-2">{selectedStudent.preferred_language || 'N/A'}</span></div>
                                        <div><span className="text-muted-foreground">Country:</span><span className="ml-2">{selectedStudent.country || 'N/A'}</span></div>
                                    </div>
                                    {selectedStudent.reminder_date && !selectedStudent.reminder_completed && (
                                        <div className="p-2 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200 dark:border-amber-800 text-sm">
                                            <span className="flex items-center gap-1 text-amber-600 font-medium"><Bell className="h-3 w-3" /> Reminder: {selectedStudent.reminder_date} {selectedStudent.reminder_time || ''}</span>
                                            {selectedStudent.reminder_note && <p className="text-xs text-muted-foreground mt-0.5">{selectedStudent.reminder_note}</p>}
                                        </div>
                                    )}
                                    {/* Color Tags */}
                                    <div className="flex items-center gap-2 flex-wrap" data-testid="bd-color-tag-picker">
                                        <span className="text-xs text-muted-foreground mr-1">Tag:</span>
                                        {Object.entries(COLOR_TAG_STYLES).map(([id, tag]) => (
                                            <button key={id}
                                                className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border transition-all ${selectedStudent.color_tag === id ? tag.color + ' ring-2 ring-offset-1 ring-current font-bold' : 'border-muted text-muted-foreground hover:border-foreground'}`}
                                                onClick={async () => {
                                                    const newTag = selectedStudent.color_tag === id ? null : id;
                                                    try {
                                                        await apiClient.patch(`/students/${selectedStudent.id}/color-tag`, { color_tag: newTag });
                                                        setSelectedStudent(prev => ({ ...prev, color_tag: newTag }));
                                                        setStudents(prev => prev.map(s => s.id === selectedStudent.id ? { ...s, color_tag: newTag } : s));
                                                    } catch { toast.error('Failed to update tag'); }
                                                }}
                                                data-testid={`bd-color-tag-btn-${id}`}>
                                                <div className={`h-2 w-2 rounded-full ${tag.dot}`} />{tag.label}
                                            </button>
                                        ))}
                                    </div>
                                </TabsContent>

                                {/* NOTES TAB — Shared between CS, Mentor, BD for collaborative follow-ups */}
                                <TabsContent value="notes" className="mt-3 space-y-3" data-testid="bd-tab-content-notes">
                                    <div className="space-y-3">
                                        <div className="flex gap-2">
                                            <MentionTextarea value={newNote} onChange={setNewNote}
                                                placeholder="Add a call note. Use @name to mention a colleague (e.g. @ashwin)"
                                                rows={3} className="flex-1 text-sm" dataTestid="bd-note-input"
                                                onSubmit={handleAddNote} />
                                            <Button size="sm" onClick={handleAddNote} disabled={!newNote.trim() || savingNote}
                                                className="self-end" data-testid="bd-save-note-btn">
                                                {savingNote ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Send className="h-3.5 w-3.5 mr-1" /> Save</>}
                                            </Button>
                                        </div>
                                        <p className="text-[11px] text-muted-foreground">
                                            Notes are shared with CS, Mentors, and BD. Use <span className="font-mono bg-pink-500/10 text-pink-700 dark:text-pink-300 px-1 rounded">@handle</span> to tag a colleague — they&apos;ll get an in-app notification.
                                        </p>
                                        <ScrollArea className="max-h-[320px]">
                                            <div className="space-y-2">
                                                {studentNotes.map(n => {
                                                    const ROLE_LABELS = {
                                                        cs_agent: 'CS', cs_head: 'CS Head', mentor: 'Mentor', master_of_academics: 'Mentor',
                                                        business_development: 'BD', bd_executive: 'BD', business_development_manager: 'BD Mgr',
                                                        super_admin: 'Admin', admin: 'Admin', sales_executive: 'Sales', team_leader: 'TL',
                                                    };
                                                    const role = (n.created_by_role || '');
                                                    const isMentor = role.includes('mentor') || role.includes('master_of_academics');
                                                    const isCS = role.includes('cs');
                                                    const isBD = role.includes('bd') || role.includes('business');
                                                    return (
                                                        <div key={n.id} data-note-id={n.id} className={`p-2.5 rounded text-sm border-l-4 transition-all ${
                                                            isMentor ? 'bg-violet-50 dark:bg-violet-950/30 border-l-violet-500'
                                                            : isCS ? 'bg-emerald-50 dark:bg-emerald-950/30 border-l-emerald-500'
                                                            : isBD ? 'bg-amber-50 dark:bg-amber-950/30 border-l-amber-500'
                                                            : 'bg-muted/50 border-l-gray-400'
                                                        }`}>
                                                            <RenderNoteBody text={n.text} />
                                                            <p className="text-[10px] text-muted-foreground mt-1.5 flex items-center gap-2">
                                                                <span className="inline-flex items-center px-1.5 py-0 rounded text-[9px] font-medium border bg-background">
                                                                    {ROLE_LABELS[role] || role || 'Agent'}
                                                                </span>
                                                                <span className="font-medium">{n.created_by_name}</span>
                                                                <span>{new Date(n.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                                                            </p>
                                                        </div>
                                                    );
                                                })}
                                                {studentNotes.length === 0 && (
                                                    <p className="text-xs text-muted-foreground py-4 text-center">No notes yet. Add the first note above — CS, Mentors and other BDs will see it.</p>
                                                )}
                                            </div>
                                        </ScrollArea>
                                    </div>
                                </TabsContent>

                                {/* TRANSACTIONS TAB */}
                                <TabsContent value="transactions" className="mt-3" data-testid="bd-tab-content-transactions">
                                    <TransactionHistory studentId={selectedStudent.id} isSuperAdmin={isSuperAdmin} />
                                </TabsContent>

                                {/* CALLS TAB */}
                                <TabsContent value="calls" className="mt-3 space-y-3" data-testid="bd-tab-content-calls">
                                    <CallRecordings phone={selectedStudent.phone} />
                                    <CallHistory contactId={selectedStudent.id} />
                                </TabsContent>

                                {/* UPDATE TAB */}
                                <TabsContent value="update" className="mt-3 space-y-4" data-testid="bd-tab-content-update">
                                    <div className="space-y-2">
                                        <Label>Move to Stage</Label>
                                        <Select value={selectedStudent.bd_stage} onValueChange={async (val) => {
                                            try {
                                                await apiClient.put(`/bd/students/${selectedStudent.id}/stage`, { bd_stage: val });
                                                setSelectedStudent(prev => ({ ...prev, bd_stage: val }));
                                                setStudents(prev => prev.map(s => s.id === selectedStudent.id ? { ...s, bd_stage: val } : s));
                                                toast.success(`Stage updated to ${BD_STAGES.find(s => s.id === val)?.label}`);
                                            } catch { toast.error('Failed to update stage'); }
                                        }}>
                                            <SelectTrigger data-testid="bd-stage-change-dropdown"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {BD_STAGES.map(st => (
                                                    <SelectItem key={st.id} value={st.id}>
                                                        <span className="flex items-center gap-1.5">
                                                            <span className={`w-2 h-2 rounded-full ${st.color}`} />
                                                            {st.label}
                                                        </span>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    {/* Pitch Type Selection */}
                                    <div className="space-y-2">
                                        <Label>Pitch Type</Label>
                                        <Select value={selectedStudent.bd_pitch_type || ''} onValueChange={async (val) => {
                                            try {
                                                const res = await apiClient.put(`/bd/students/${selectedStudent.id}/stage`, {
                                                    bd_stage: selectedStudent.bd_stage,
                                                    pitch_type: val,
                                                });
                                                // Backend auto-promotes pre-pitch stages → "pitched" when a pitch
                                                // type is set. Mirror that locally so the kanban card moves
                                                // immediately without waiting for a refetch.
                                                const updatedStage = res?.data?.bd_stage || selectedStudent.bd_stage;
                                                setSelectedStudent(prev => ({ ...prev, bd_pitch_type: val, bd_stage: updatedStage }));
                                                setStudents(prev => prev.map(s => s.id === selectedStudent.id ? { ...s, bd_pitch_type: val, bd_stage: updatedStage } : s));
                                                toast.success(updatedStage === 'pitched' && selectedStudent.bd_stage !== 'pitched'
                                                    ? `Moved to Pitched · ${PITCH_TYPE_STYLES[val]?.label}`
                                                    : `Pitch type set to ${PITCH_TYPE_STYLES[val]?.label}`);
                                            } catch { toast.error('Failed to update pitch type'); }
                                        }}>
                                            <SelectTrigger data-testid="bd-pitch-type-dropdown"><SelectValue placeholder="Select pitch type..." /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="redeposit">
                                                    <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" />Redeposit Only</span>
                                                </SelectItem>
                                                <SelectItem value="upgrade">
                                                    <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-violet-500" />Upgrade Only</span>
                                                </SelectItem>
                                                <SelectItem value="mix">
                                                    <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-500" />Redeposit + Upgrade (Mix)</span>
                                                </SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    {/* Amount fields for Mix type */}
                                    {selectedStudent.bd_pitch_type === 'mix' && (
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="space-y-1">
                                                <Label className="text-xs">Redeposit Amount (AED)</Label>
                                                <Input type="number" placeholder="e.g. 3000"
                                                    defaultValue={selectedStudent.bd_pitch_redeposit_amount || ''}
                                                    onBlur={async (e) => {
                                                        const val = parseFloat(e.target.value);
                                                        if (!val) return;
                                                        try {
                                                            await apiClient.put(`/bd/students/${selectedStudent.id}/stage`, {
                                                                bd_stage: selectedStudent.bd_stage,
                                                                pitch_type: 'mix',
                                                                pitch_redeposit_amount: val,
                                                            });
                                                            setSelectedStudent(prev => ({ ...prev, bd_pitch_redeposit_amount: val }));
                                                        } catch { /* silent */ }
                                                    }}
                                                    data-testid="bd-pitch-redeposit-amount" />
                                            </div>
                                            <div className="space-y-1">
                                                <Label className="text-xs">Upgrade Amount (AED)</Label>
                                                <Input type="number" placeholder="e.g. 5000"
                                                    defaultValue={selectedStudent.bd_pitch_upgrade_amount || ''}
                                                    onBlur={async (e) => {
                                                        const val = parseFloat(e.target.value);
                                                        if (!val) return;
                                                        try {
                                                            await apiClient.put(`/bd/students/${selectedStudent.id}/stage`, {
                                                                bd_stage: selectedStudent.bd_stage,
                                                                pitch_type: 'mix',
                                                                pitch_upgrade_amount: val,
                                                            });
                                                            setSelectedStudent(prev => ({ ...prev, bd_pitch_upgrade_amount: val }));
                                                        } catch { /* silent */ }
                                                    }}
                                                    data-testid="bd-pitch-upgrade-amount" />
                                            </div>
                                        </div>
                                    )}

                                    <DialogFooter className="pt-2">
                                        <Button variant="outline" onClick={() => setShowDetailModal(false)}>Close</Button>
                                        <Button onClick={() => {
                                            // Pre-fill deposit type from pitch type
                                            setRedepositData(p => ({ ...p, deposit_type: selectedStudent?.bd_pitch_type || 'redeposit' }));
                                            setShowRedepositModal(true);
                                            setShowDetailModal(false);
                                        }}
                                            data-testid="bd-record-redeposit-btn">
                                            <DollarSign className="h-4 w-4 mr-2" /> {
                                                selectedStudent?.bd_pitch_type === 'upgrade' ? 'Record Upgrade' :
                                                selectedStudent?.bd_pitch_type === 'mix' ? 'Record Revenue' :
                                                'Record Redeposit'
                                            }
                                        </Button>
                                    </DialogFooter>
                                </TabsContent>
                            </Tabs>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Record Redeposit Modal */}
            <Dialog open={showRedepositModal} onOpenChange={setShowRedepositModal}>
                <DialogContent className="max-w-sm" data-testid="bd-redeposit-modal">
                    <DialogHeader>
                        <DialogTitle>Record Transaction</DialogTitle>
                        <DialogDescription>For {selectedStudent?.full_name}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Transaction Type</Label>
                            <Select value={redepositData.deposit_type} onValueChange={val => setRedepositData(p => ({ ...p, deposit_type: val }))}>
                                <SelectTrigger data-testid="bd-redeposit-type"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="redeposit">
                                        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" />Redeposit</span>
                                    </SelectItem>
                                    <SelectItem value="upgrade">
                                        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-violet-500" />Upgrade</span>
                                    </SelectItem>
                                    <SelectItem value="mix">
                                        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-500" />Mix (Redeposit + Upgrade)</span>
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Amount (AED)</Label>
                            <Input type="number" placeholder="e.g. 5000" value={redepositData.amount_aed}
                                onChange={e => setRedepositData(p => ({ ...p, amount_aed: e.target.value }))}
                                data-testid="bd-redeposit-amount" />
                        </div>
                        <div>
                            <Label>Date</Label>
                            <Input type="date" value={redepositData.date}
                                onChange={e => setRedepositData(p => ({ ...p, date: e.target.value }))}
                                data-testid="bd-redeposit-date" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowRedepositModal(false)} disabled={recordingRedeposit}>Cancel</Button>
                        <Button onClick={handleRecordRedeposit}
                            disabled={!redepositData.amount_aed || recordingRedeposit}
                            data-testid="bd-redeposit-confirm-btn">
                            {recordingRedeposit
                                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Recording…</>
                                : `Confirm ${redepositData.deposit_type === 'upgrade' ? 'Upgrade' : redepositData.deposit_type === 'mix' ? 'Mix' : 'Redeposit'}`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reminder Modal */}
            <ReminderModal
                open={showReminderModal}
                onClose={() => setShowReminderModal(false)}
                entityType="student"
                entityId={selectedStudent?.id}
                entityName={selectedStudent?.full_name}
                onSuccess={handleReminderSuccess}
            />
        </div>
    );
}
